/**
 * Schedules page
 * Lists all recurring scrape schedules with management actions
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Table, Tag, Space, Popconfirm, Typography, Tooltip, Input, Select, Modal, Form } from 'antd';
import {
  PlusOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  ThunderboltOutlined,
  DeleteOutlined,
  EditOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { PageHeader } from '@/components/common/PageHeader';
import {
  useSchedules,
  usePauseSchedule,
  useResumeSchedule,
  useTriggerSchedule,
  useDeleteSchedule,
  useUpdateSchedule,
} from '@/hooks/useSchedules';
import type { Schedule, ScheduleStatus, IntervalPreset, UpdateScheduleInput } from '@/types/schedule';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const { Text } = Typography;

const STATUS_COLORS: Record<ScheduleStatus, string> = {
  active: 'green',
  paused: 'orange',
  deleted: 'red',
};

const PRESET_LABELS: Record<IntervalPreset, string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  biweekly: 'Biweekly',
  monthly: 'Monthly',
  custom: 'Custom',
};

const PRESET_OPTIONS = [
  { value: 'daily', label: 'Daily (3:00 AM UTC)' },
  { value: 'weekly', label: 'Weekly (Mon 3:00 AM UTC)' },
  { value: 'biweekly', label: 'Biweekly (1st & 15th)' },
  { value: 'monthly', label: 'Monthly (1st of month)' },
  { value: 'custom', label: 'Custom cron' },
];

export function SchedulesPage() {
  const navigate = useNavigate();
  const { data: schedules, isLoading } = useSchedules();
  const pauseSchedule = usePauseSchedule();
  const resumeSchedule = useResumeSchedule();
  const triggerSchedule = useTriggerSchedule();
  const deleteSchedule = useDeleteSchedule();
  const updateSchedule = useUpdateSchedule();

  const [editingSchedule, setEditingSchedule] = useState<Schedule | null>(null);
  const [editForm, setEditForm] = useState<UpdateScheduleInput>({});

  // Track which row is currently being acted on
  const isTriggeringId = triggerSchedule.isPending ? triggerSchedule.variables : null;
  const isPausingId = pauseSchedule.isPending ? pauseSchedule.variables : null;
  const isResumingId = resumeSchedule.isPending ? resumeSchedule.variables : null;

  const openEdit = (schedule: Schedule) => {
    setEditingSchedule(schedule);
    setEditForm({
      name: schedule.name,
      searchQuery: schedule.searchQuery,
      intervalPreset: schedule.intervalPreset,
      cronExpression: schedule.cronExpression,
    });
  };

  const handleEditSave = () => {
    if (!editingSchedule) return;
    updateSchedule.mutate(
      { id: editingSchedule.id, input: editForm },
      {
        onSuccess: () => setEditingSchedule(null),
      }
    );
  };

  const columns: ColumnsType<Schedule> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record) => (
        <div>
          <Text strong>{name}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>{record.searchQuery}</Text>
        </div>
      ),
    },
    {
      title: 'Location',
      dataIndex: 'locationName',
      key: 'locationName',
      render: (val: string | undefined) => val || <Text type="secondary">-</Text>,
    },
    {
      title: 'Interval',
      key: 'interval',
      render: (_: unknown, record) => (
        <div>
          <Text>{PRESET_LABELS[record.intervalPreset]}</Text>
          {record.intervalPreset === 'custom' && (
            <>
              <br />
              <Text type="secondary" style={{ fontSize: 11, fontFamily: 'monospace' }}>
                {record.cronExpression}
              </Text>
            </>
          )}
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: ScheduleStatus) => (
        <Tag color={STATUS_COLORS[status]}>
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </Tag>
      ),
    },
    {
      title: 'Next Run',
      dataIndex: 'nextRunAt',
      key: 'nextRunAt',
      render: (val: string | null, record) => {
        if (record.status !== 'active' || !val) return <Text type="secondary">-</Text>;
        return (
          <Tooltip title={dayjs(val).format('YYYY-MM-DD HH:mm:ss')}>
            <Text>{dayjs(val).fromNow()}</Text>
          </Tooltip>
        );
      },
    },
    {
      title: 'Last Run',
      dataIndex: 'lastRunAt',
      key: 'lastRunAt',
      render: (val: string | null) => {
        if (!val) return <Text type="secondary">Never</Text>;
        return (
          <Tooltip title={dayjs(val).format('YYYY-MM-DD HH:mm:ss')}>
            <Text>{dayjs(val).fromNow()}</Text>
          </Tooltip>
        );
      },
    },
    {
      title: 'Runs',
      dataIndex: 'totalRuns',
      key: 'totalRuns',
      width: 80,
      align: 'center',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (_: unknown, record) => (
        <Space size={4}>
          <Tooltip title="Edit">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => openEdit(record)}
            />
          </Tooltip>

          <Tooltip title="Trigger Now">
            <Button
              type="text"
              size="small"
              icon={<ThunderboltOutlined />}
              disabled={record.status !== 'active'}
              loading={isTriggeringId === record.id}
              onClick={() => triggerSchedule.mutate(record.id)}
            />
          </Tooltip>

          {record.status === 'active' ? (
            <Tooltip title="Pause">
              <Button
                type="text"
                size="small"
                icon={<PauseCircleOutlined />}
                loading={isPausingId === record.id}
                onClick={() => pauseSchedule.mutate(record.id)}
              />
            </Tooltip>
          ) : record.status === 'paused' ? (
            <Tooltip title="Resume">
              <Button
                type="text"
                size="small"
                icon={<PlayCircleOutlined />}
                loading={isResumingId === record.id}
                onClick={() => resumeSchedule.mutate(record.id)}
              />
            </Tooltip>
          ) : null}

          <Popconfirm
            title="Delete this schedule?"
            description="This will not delete jobs already created by this schedule."
            onConfirm={() => deleteSchedule.mutate(record.id)}
            okText="Delete"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Delete">
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Schedules"
        breadcrumbs={[
          { title: 'Dashboard', href: '/' },
          { title: 'Schedules' },
        ]}
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/schedules/new')}
          >
            New Schedule
          </Button>
        }
      />

      <Table
        columns={columns}
        dataSource={schedules}
        rowKey="id"
        loading={isLoading}
        pagination={false}
        locale={{ emptyText: 'No schedules yet. Create one to automate recurring scrapes.' }}
      />

      <Modal
        title="Edit Schedule"
        open={!!editingSchedule}
        onOk={handleEditSave}
        onCancel={() => setEditingSchedule(null)}
        confirmLoading={updateSchedule.isPending}
        okText="Save"
      >
        {editingSchedule && (
          <Form layout="vertical" style={{ marginTop: 16 }}>
            <Form.Item label="Name">
              <Input
                value={editForm.name}
                onChange={(e) => setEditForm((prev) => ({ ...prev, name: e.target.value }))}
              />
            </Form.Item>
            <Form.Item label="Search Query">
              <Input
                value={editForm.searchQuery}
                onChange={(e) => setEditForm((prev) => ({ ...prev, searchQuery: e.target.value }))}
              />
            </Form.Item>
            <Form.Item label="Interval">
              <Select
                value={editForm.intervalPreset}
                onChange={(val) => setEditForm((prev) => ({ ...prev, intervalPreset: val as IntervalPreset }))}
                options={PRESET_OPTIONS}
              />
            </Form.Item>
            {editForm.intervalPreset === 'custom' && (
              <Form.Item label="Cron Expression">
                <Input
                  value={editForm.cronExpression}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, cronExpression: e.target.value }))}
                  placeholder="0 3 * * 1"
                  style={{ fontFamily: 'monospace' }}
                />
              </Form.Item>
            )}
          </Form>
        )}
      </Modal>
    </div>
  );
}

export default SchedulesPage;
